/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { ProvisionDevicesResponseV2 } from "@tivocorp/data-domain-lib";

import { DeviceModel } from "../device";
import { Log } from "../logging";
import { SignInInteractor } from "../interactor";
import { RetryMechanism } from "../retrymechanism";
import { createSilentRetryOptions } from "../retrymechanism/silentRetryOptions";
import type { IProvisioningStrategy } from "./IProvisioningStrategy";
import { TokenManager } from "./TokenManager";

const TAG: string = "auth/DeviceProvisioningStrategy";

/** Persists a provisioning response — bodyId / partner ids, token re-key. */
export type TPersistProvisionData = (
  response: ProvisionDevicesResponseV2
) => Promise<void>;

/**
 * Recovery for DPS `410 AUTHS.DLID_PERMANENTLY_REMOVED`: the backend deleted
 * this device's identity, so every DPS request carrying it fails forever and
 * the session built on it cannot be repaired in place.
 */
export interface TIdentityRemovalHandler {
  /** Whether a DPS failure is the identity-removed 410. */
  matches(error: unknown): boolean;
  /** Replaces the identity and sends the user back to sign-in. */
  recover(): Promise<void>;
}

/**
 * Re-runs `POST v2/provisionDevices` outside sign-in, with the same request the
 * sign-in flow that provisioned this device sent: the authenticationType it
 * persisted as the last auth context, the device logging id, and the current
 * domain token.
 *
 * Runs unseen, ahead of a replayed CloudCore call, so it uses the silent retry
 * ladder — a transient failure is retried briefly, a definitive one is not.
 *
 * A 410 identity-removed failure is handed to `identityRemoval` before it is
 * re-thrown. Sign-in has its own recovery for it; after sign-in this is the
 * only place DPS runs, so without it the device would loop on the 410 with no
 * way back to sign-in — even across restarts, since a token refresh never
 * touches DPS.
 *
 * `persist` and `identityRemoval` are injected because both live with the
 * sign-in flows they were written for, in the view-model layer.
 */
export class DeviceProvisioningStrategy implements IProvisioningStrategy {
  private readonly mPersist: TPersistProvisionData;
  private readonly mIdentityRemoval: TIdentityRemovalHandler;

  public constructor(
    persist: TPersistProvisionData,
    identityRemoval: TIdentityRemovalHandler
  ) {
    this.mPersist = persist;
    this.mIdentityRemoval = identityRemoval;
  }

  public async provision(): Promise<void> {
    const lastAuthContext = await DeviceModel.getLastAuthContext();
    if (lastAuthContext === undefined || lastAuthContext === null) {
      throw new Error("No auth context to re-provision the device with");
    }

    Log.d(TAG, "provision() re-provisioning the device");
    const signInInteractor: SignInInteractor = new SignInInteractor();
    const domainToken: string = await TokenManager.getValidAccessToken();
    let response: ProvisionDevicesResponseV2;
    try {
      response =
        await RetryMechanism.callServiceWithRetry<ProvisionDevicesResponseV2>(
          () =>
            signInInteractor.createProvisionDevicesV2(
              {
                provisionDevicesRequestV2: {
                  authenticationType: lastAuthContext.authenticationType,
                  deviceLoggingId: DeviceModel.getDeviceLoggingId(),
                  deviceConfig: {}
                }
              },
              domainToken
            ),
          [],
          createSilentRetryOptions("POST", (error: unknown): boolean =>
            this.mIdentityRemoval.matches(error)
          )
        );
    } catch (error: unknown) {
      if (this.mIdentityRemoval.matches(error)) {
        Log.w(TAG, "provision() device identity removed server-side");
        await this.mIdentityRemoval.recover();
      }
      throw error;
    }

    // The provisioning payload carries bodyId / partnerId — do not log it.
    await this.mPersist(response);
    Log.d(TAG, "provision() device re-provisioned");
  }
}
